import { Button, Image } from '@mantine/core';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import logo from '@/assets/logo.png';
import { verifyEmail } from '@mygpt/app';
import { saveTokenInfo } from '@/utils/tool';
import { TokenInfo } from '@/types/interface';
import { useLogin } from '@/hooks/useLogin';

type VerifyStatus = 'idle' | 'success' | 'error';

const toTokenInfo = (data: any): TokenInfo | null => {
  if (!data) return null;
  const loginToken = data.loginToken;
  if (loginToken?.accessToken && loginToken?.tokenType && loginToken?.accessTokenExpiresIn) {
    return {
      access_token: loginToken.accessToken,
      refresh_token: loginToken.refreshToken ?? '',
      expires_in: loginToken.accessTokenExpiresIn,
      token_type: loginToken.tokenType,
      scope: '',
    };
  }

  if (data.access_token && data.token_type && data.expires_in) {
    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token ?? '',
      expires_in: Number(data.expires_in),
      token_type: data.token_type,
      scope: data.scope ?? '',
    };
  }

  return null;
};

const VerifyEmailPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { scheduleTokenRenewal } = useLogin();

  const [status, setStatus] = useState<VerifyStatus>('idle');
  const [message, setMessage] = useState(t('verify_email.verifying'));
  const [isVerifying, setIsVerifying] = useState(false);

  const runVerification = useCallback(async () => {
    console.log('runVerification, token', token);
    if (!token) {
      setStatus('error');
      setMessage(t('verify_email.missing_token'));
      return;
    }

    setIsVerifying(true);
    setStatus('idle');
    setMessage(t('verify_email.verifying'));

    try {
      const res = await verifyEmail({ token });
      console.log('verifyEmail: res', res, JSON.stringify(res));
      const tokenInfo = toTokenInfo(res?.data);
      if (tokenInfo) {
        saveTokenInfo(tokenInfo);
        scheduleTokenRenewal();
      }
      setStatus('success');
      setMessage(t('verify_email.success'));
    } catch (error: any) {
      setStatus('error');
      const fallbackMessage = t('verify_email.error');
      setMessage((typeof error === 'object' && (error?.errorMsg || error?.message)) || fallbackMessage);
    } finally {
      setIsVerifying(false);
    }
  }, [scheduleTokenRenewal, t, token]);

  useEffect(() => {
    runVerification();
  }, []);

  const handleRetry = () => {
    if (isVerifying) return;
    runVerification();
  };

  const titleText = t('verify_email.title');
  const descriptionText = t('verify_email.description');
  const goHomeText = t('verify_email.go_to_home');
  const retryText = t('verify_email.retry');
  const pendingTitle = t('verify_email.pending_title');
  const successTitle = t('verify_email.success_title');
  const errorTitle = t('verify_email.error_title');

  const renderStatusIcon = () => {
    if (status === 'success') {
      return <CheckCircleIcon className="h-12 w-12 text-green-500" />;
    }
    if (status === 'error') {
      return <ExclamationCircleIcon className="h-12 w-12 text-red-500" />;
    }
    return <div className="h-12 w-12 rounded-full border-4 border-primary-100 border-t-primary-500 animate-spin" />;
  };

  const currentTitle = status === 'success' ? successTitle : status === 'error' ? errorTitle : pendingTitle;

  return (
    <div className="min-h-screen flex flex-col justify-center bg-gray-50 py-4 px-4 md:py-12 md:px-6 lg:px-8 relative">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Image src={logo} className="mx-auto h-10 w-auto md:h-12" fit="contain" />

        <h2 className="mt-4 md:mt-6 text-center text-2xl md:text-3xl font-bold tracking-tight text-gray-900">
          {titleText}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">{descriptionText}</p>
      </div>

      <div className="mt-6 md:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="space-y-6 bg-white py-8 px-6 shadow rounded-xl border border-gray-200 sm:px-10 text-center">
          <div className="flex flex-col items-center space-y-4">
            {renderStatusIcon()}
            <div>
              <h3 className="text-xl font-semibold text-gray-900">{currentTitle}</h3>
              <p className="mt-2 text-sm text-gray-600">{message}</p>
            </div>
          </div>

          <div className="space-y-3">
            {status === 'success' && (
              <Button
                component={Link}
                to="/"
                fullWidth
                size="md"
                radius="md"
                variant="outline"
                className="font-medium text-base md:text-sm py-3"
              >
                {goHomeText}
              </Button>
            )}
            {status === 'error' && (
              <Button
                variant="outline"
                fullWidth
                size="md"
                radius="md"
                onClick={handleRetry}
                loading={isVerifying}
                className="font-medium text-base md:text-sm py-3"
              >
                {retryText}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
